import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Brain, Cpu, FileText, Globe, Landmark, MessageSquare, PhoneCall, Settings2, Users } from "lucide-react";
import { apiGet } from "@/lib/apiAdmin";
import { toast } from "sonner";
import Logo from "@/components/landing/Logo";
import { JarvisOrb } from "./JarvisOrb";
import { JarvisChat } from "./JarvisChat";
import { JarvisCommand } from "./JarvisCommand";
import { JarvisApprovals } from "./JarvisApprovals";
import { JarvisComms } from "./JarvisComms";
import { JarvisSettings } from "./JarvisSettings";
import { JarvisInvoices } from "./JarvisInvoices";
import { JarvisBrain } from "./JarvisBrain";
import { JarvisWebsite } from "./JarvisWebsite";
import { JarvisCustomers } from "./JarvisCustomers";
import { JarvisBank } from "./JarvisBank";
import { HUD } from "./theme";

const SUB = [
  { k: "command", label: "Command", Icon: Cpu },
  { k: "approvals", label: "Approvals", Icon: MessageSquare },
  { k: "comms", label: "Calls & Texts", Icon: PhoneCall },
  { k: "invoices", label: "Invoices", Icon: FileText },
  { k: "customers", label: "Customers", Icon: Users },
  { k: "money", label: "Money", Icon: Landmark },
  { k: "website", label: "Website", Icon: Globe },
  { k: "brain", label: "Brain", Icon: Brain },
  { k: "settings", label: "Settings", Icon: Settings2 },
];

const greeting = () => {
  const h = Number(new Date().toLocaleString("en-US", { timeZone: "America/Phoenix", hour: "numeric", hour12: false })) % 24;
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const HudBackdrop = () => (
  <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-3xl">
    <div className="absolute inset-0 bg-[#05080C]" />
    <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(rgba(34,229,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(34,229,255,.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
    <div className="absolute -top-40 -left-20 w-[520px] h-[520px] rounded-full bg-cyan-500/10 blur-[120px]" />
    <div className="absolute -bottom-40 right-0 w-[420px] h-[420px] rounded-full bg-violet-600/10 blur-[120px]" />
  </div>
);

export default function JarvisHUD() {
  const [sub, setSub] = useState("command");
  const [st, setSt] = useState(null);
  const [tick, setTick] = useState(0);
  const refresh = () => {
    setTick((t) => t + 1);
    apiGet("/admin/jarvis/status").then(setSt).catch(() => {});
  };
  useEffect(() => {
    refresh();
    const bank = new URLSearchParams(window.location.search).get("bank");
    if (bank) {
      setSub("money");
      bank === "connected" ? toast.success("Bank connected, importing transactions") : toast.error("Bank connection was not completed");
      window.history.replaceState({}, "", window.location.pathname);
    }
    const g = new URLSearchParams(window.location.search).get("google");
    if (g) {
      setSub("settings");
      g === "connected" ? toast.success("Google Calendar connected") : toast.error("Google connection failed — check Client ID/Secret and redirect URI");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  return (
    <div className="relative isolate rounded-3xl border border-cyan-400/10 p-4 sm:p-6 -mx-2 sm:mx-0" data-testid="jarvis-hud">
      <HudBackdrop />
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center gap-4">
          <JarvisOrb size={64} />
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Logo size={26} showWordmark />
              <span className={HUD.label}>Command center</span>
            </div>
            <motion.h2 initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="font-['Outfit'] text-2xl sm:text-3xl text-white font-semibold" data-testid="jarvis-greeting">
              {greeting()}, <span className="text-cyan-300">{st?.owner_title || "sir"}</span>.
            </motion.h2>
            <p className={`${HUD.mono} text-[11px] text-gray-400 tracking-wider`} data-testid="jarvis-name">{st?.assistant_name || "JARVIS-OPS-9"} · ALL SYSTEMS NOMINAL</p>
          </div>
        </div>
        <div className="flex gap-1 p-1 rounded-full border border-cyan-400/10 bg-black/40 overflow-x-auto scrollbar-hide self-start max-w-full">
          {SUB.map(({ k, label, Icon }) => (
            <button key={k} onClick={() => setSub(k)} data-testid={`jarvis-sub-${k}`} className={`relative h-9 px-4 rounded-full text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${sub === k ? "text-black" : "text-gray-400 hover:text-white"}`}>
              {sub === k && <motion.span layoutId="jarvis-sub-pill" className="absolute inset-0 rounded-full bg-cyan-400" />}
              <Icon className="w-4 h-4 relative" /> <span className="relative">{label}</span>
              {k === "approvals" && st?.stats?.drafts > 0 && <span className="relative text-[10px] px-1.5 rounded-full bg-amber-400 text-black">{st.stats.drafts}</span>}
            </button>
          ))}
        </div>
      </div>

      {sub === "command" ? (
        <div className="grid xl:grid-cols-[1fr_440px] gap-4">
          <JarvisCommand st={st} tick={tick} onRefresh={refresh} />
          <JarvisChat
            name={st?.assistant_name}
            title={st?.owner_title}
            speakDefault={st?.speak_replies ?? true}
            onActivity={refresh}
            className="h-[640px] xl:h-auto xl:min-h-[640px] xl:max-h-[calc(100vh-220px)] xl:sticky xl:top-[140px]"
          />
        </div>
      ) : sub === "approvals" ? (
        <JarvisApprovals onChange={refresh} />
      ) : sub === "comms" ? (
        <JarvisComms />
      ) : sub === "invoices" ? (
        <JarvisInvoices />
      ) : sub === "customers" ? (
        <JarvisCustomers />
      ) : sub === "money" ? (
        <JarvisBank />
      ) : sub === "website" ? (
        <JarvisWebsite />
      ) : sub === "brain" ? (
        <JarvisBrain />
      ) : (
        <JarvisSettings onSaved={refresh} />
      )}
    </div>
  );
}
